import { DeferredUpdater, profiler } from '@dinosband/dbi-utils'
import { Message, MessageRange } from './types'
import { MessageMemCache } from './message-mem-cache'

//--------------------------------------------------------------------------
// MessageCacheBase
//--------------------------------------------------------------------------
export abstract class MessageCacheBase {
  maxCount: number
  protected memCaches: Map<number, MessageMemCache>

  protected updater: DeferredUpdater

  protected abstract _reset (): Promise<void>
  protected abstract _load (chatRoomId: number): Promise<Message[]>
  protected abstract _put (mesgs: Message[]): Promise<void>
  protected abstract _delete (mesgs: Message[]): Promise<void>

  constructor (maxCount: number, updateDelay = 3000) {
    this.maxCount = maxCount
    this.memCaches = new Map<number, MessageMemCache>()

    this.updater = new DeferredUpdater(async () => {
      await this.flush()
    }, updateDelay)
  }

  protected async getCache (chatRoomId: number) {
    let cache = this.memCaches.get(chatRoomId)
    if (!cache) {
      cache = new MessageMemCache()
      this.memCaches.set(chatRoomId, cache)
      const mesgs = await this._load(chatRoomId)
      cache.load(mesgs)
    }
    return cache
  }

  async getRange (chatRoomId: number): Promise<MessageRange> {
    const cache = await this.getCache(chatRoomId)
    return {
      min: cache.minKey || -1,
      max: cache.maxKey || -1,
    }
  }

  async reset (chatRoomId?: number): Promise<void> {
    if (chatRoomId) {
      const cache = await this.getCache(chatRoomId)
      cache.reset()
    } else {
      const caches = Array.from(this.memCaches.values())
      for (const cache of caches) {
        // clear mem caches
        cache.reset()
      }

      // clear DB
      await this._reset()
    }
  }

  read (chatRoomId: number, seq: number) { // not check validity
    const cache = this.memCaches.get(chatRoomId)
    if (!cache) {
      return null
    }
    return cache._read(seq)
  }

  async delete (chatRoomId: number, seq: number) { // don't use!, for development only
    const cache = await this.getCache(chatRoomId)
    cache.delete({ chatRoomId, seq } as Message)
  }

  readBulk (chatRoomId: number, seqs: number[]): Message[] { // synchronous read func for message-loader
    const cache = this.memCaches.get(chatRoomId)
    if (!cache) {
      return []
    }
    return cache._readBulk(seqs)
  }

  isReservedToDelete (chatRoomId: number, seq: number) {
    const cache = this.memCaches.get(chatRoomId)
    if (!cache) {
      return false
    }
    return cache._isReservedToDelete(this.maxCount, seq)
  }

  async trim (chatRoomId: number, predicate: (message: Message, excessCount: number) => { del: boolean, stop?: boolean}) {
    const cache = await this.getCache(chatRoomId)
    cache._trim(this.maxCount, predicate)
  }

  async readBulkByIds (chatRoomId: number, ids: number[]): Promise<Message[]> {
    const cache = await this.getCache(chatRoomId)
    return ids.map((i) => cache._read(i, true))
      .filter((i) => i) as Message[]
  }

  async get (chatRoomId: number, inMesg: Message, byId = false): Promise<Message | null> {
    const cache = await this.getCache(chatRoomId)
    const cached = cache.get(inMesg, byId)
    if (cached) {
      if (cached.updatedAt === inMesg.updatedAt) {
        return cached
      } else {
        cache.delete(inMesg)
        this.updater.touch()
      }
    }
    return null
  }

  async getById (chatRoomId: number, messageId: number): Promise<Message | null> {
    const cache = await this.getCache(chatRoomId)
    return cache.getById(messageId)
  }

  async contents (chatRoomId: number) {
    const cache = await this.getCache(chatRoomId)
    return cache.contents
  }

  async put (chatRoomId: number, mesg: Message): Promise<void> {
    return await this.bulkPut(chatRoomId, [mesg])
  }

  async bulkPut (chatRoomId: number, mesgs: Message[]): Promise<void> {
    const cache = await this.getCache(chatRoomId)

    mesgs.forEach((mesg) => {
      if (!mesg.props) {
        console.error('cache/put', chatRoomId, mesg.id, mesg.seq)
      }
      if (!mesg.seq) {
        return
      }
      const verifiedAt = Date.now()
      // const cached = cache.get(mesg)
      // if (cached) {
      //   if (cached.updatedAt === mesg.updatedAt && cached.props) {
      //     // console.error('cache/put (exist): ', chatRoomId, mesg.seq)
      //     return
      //   }
      // }
      cache.put({ ...mesg, verifiedAt })
      this.updater.touch()
    })
  }

  protected async flush (): Promise<void> {
    profiler.of('message-cache.flush').begin()
    const listDelete: Message[] = []
    const listPut: Message[] = []

    this.memCaches.forEach((cache, chatRoomId) => {
      // CAUTION: deleteOlds() should be called before getModified()
      const toDelete = cache.deleteOlds(this.maxCount)
      listDelete.push(...toDelete)
      if (toDelete.length) {
      }

      const toPut = cache.getModified()
      listPut.push(...toPut)
      if (toPut.length) {
      }
    })

    if (listPut.length) {
      await this._put(listPut)
    }
    if (listDelete.length) {
      await this._delete(listDelete)
    }
    profiler.of('message-cache.flush').end()
  }
}
